import { Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { AppService } from './app.service';
import { BaseService } from './base.service';
import { workflow_mappings } from '../../components/kx-ai-chat-embed/kx-ai-chat.model';

@Injectable({
  providedIn: 'root'
})
export class AccountContextService {
  private mapping: workflow_mappings | null = null;
  private _mappingPromise: Promise<workflow_mappings | null> | null = null;
  private embedToken: { clientId: string; flowId: string; token: string } | null = null;

  constructor(
    private appService: AppService,
    private baseService: BaseService
  ) { }

  /**
   * Resolves the logged-in user's account. GET_MY_CONFIG returns account_id/account_name for
   * every user type and parseMyConfigObject() copies them into _currentSessionData, so no extra
   * lookup is needed. The organization / patientDetails fallbacks only cover the window before
   * config has been parsed (e.g. routes that never run parseMyConfigObject()).
   */
  public getAccountDetails(): Promise<{ account_id: string, account_name: string } | null> {
    const session = this.appService?._currentSessionData;
    if (session?.account_id) {
      return Promise.resolve({ account_id: session.account_id, account_name: session.account_name || '' });
    }

    const org = this.appService?.getOrganization();
    if (org?.account_id) {
      return Promise.resolve({ account_id: org.account_id, account_name: org.account_name });
    }

    try {
      const patientDetailsStr = localStorage.getItem("patientDetails");
      if (patientDetailsStr) {
        const patientDetails = JSON.parse(patientDetailsStr);
        if (patientDetails?.account_id) {
          return Promise.resolve({
            account_id: patientDetails.account_id,
            account_name: patientDetails.account_name
          });
        }
      }
    } catch (e) {
      // ignore
    }

    return Promise.resolve(null);
  }

  getWorkflowMapping(): workflow_mappings | null {
    return this.mapping;
  }

  setWorkflowMapping(response: workflow_mappings): void {
    this.mapping = response ?? {
      flow_id: undefined
    };
  }

  /**
   * Resolves the account/role's workflow mapping, fetching it at most once. Concurrent callers
   * share the same in-flight request, so overlapping init triggers can't duplicate the
   * GET_WORKFLOW_MAPPINGS_BY_ACCOUNT call.
   */
  fetchWorkflowMapping(): Promise<workflow_mappings | null> {
    if (this.mapping) {
      return Promise.resolve(this.mapping);
    }
    if (this._mappingPromise) {
      return this._mappingPromise;
    }

    this._mappingPromise = firstValueFrom(this.baseService.getByDataSource({
      application: 'serviceworkflow',
      query_id: 'GET_WORKFLOW_MAPPINGS_BY_ACCOUNT',
      resource: 'workflow_mappings'
    })).then((response: any) => {
      this.setWorkflowMapping(response?.resource?.[0]);
      return this.mapping;
    }).catch(error => {
      console.error('Failed to fetch workflow mapping', error);
      // Clear so a later attempt can retry rather than being stuck with a failed lookup.
      this._mappingPromise = null;
      return null;
    });

    return this._mappingPromise;
  }

  getEmbedToken(clientId: string, flowId: string): string | null {
    if (this.embedToken?.clientId === clientId && this.embedToken?.flowId === flowId) {
      return this.embedToken.token;
    }
    return null;
  }

  setEmbedToken(clientId: string, flowId: string, token: string): void {
    this.embedToken = { clientId, flowId, token };
  }

  /**
   * Clears account/session-scoped caches (workflow mapping, embed token). Call this whenever
   * the authenticated user changes (login/logout) in the same tab, since this service is a
   * root singleton that otherwise survives across sessions and would leak the previous
   * account/role's cached data.
   */
  resetWorkflowState(): void {
    this.mapping = null;
    this._mappingPromise = null;
    this.embedToken = null;
  }
}
